import React, { useEffect, useState } from "react";
import * as R from "ramda";
import Translator from "Languages/Utility/Translator";
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
} from "@material-ui/core";
import Input from "Components/Input/Input";
import MuiButton from "Components/MuiButton/MuiButton";
import Grid from "@material-ui/core/Grid";
import * as Icons from "react-bootstrap-icons";
import { useSetState } from "Helpers/Hooks";
import { read } from "Helpers/LocalStorage";
import { useTranslation } from "react-i18next";
import { satc_accountingHistoryActive } from "Constants/TableColumns";
import RemoteTable from "Components/RemoteTable/RemoteTable";
import MuiAutoComplete from "Components/MuiAutoComplete/MuiAutoComplete";
import MuiCheckBoxGroup from "Components/MuiCheckboxGroup/MuiCheckboxGroup";
import { useDispatch, useSelector } from "react-redux";
import { getActivePeriods } from "Stores/actions";
import AccountService from "Services/http/endpoints/superadmin/AccountsEndpoints";
import showNotify from "Helpers/Notify";
import InnerCircularProgress from "Components/InnerCircularProgress/InnerCircularProgress";
import StatusHandler from "Helpers/StatusHandler";
import addNumToList from "Helpers/addNumToList";
import { convertToEnglishNumber } from "Helpers/convertToEnglishNumber";
import { useTheme } from "@material-ui/core/styles";

export default function NewIncTime({ disabled, onCallBack }) {
  const { direction } = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [sizePerPage, setSizePerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showDialog, setShowDialog] = useState(false);
  const [formValid, setFormValid] = useState({});
  const [checkValid, setCheckValid] = useState(false);
  const [allowAction, setAllowAction] = useState(false);
  const [tblData, setTblData] = useState([]);
  const [ids, setIds] = useState([]);

  const { allGroups, activePeriods } = useSelector((state) => ({
    allGroups: state.saAccounts.allGroups,
    activePeriods: state.saAccounts.activePeriods,
  }));
  const [controls, setControls] = useSetState({
    hoursNumber: {
      type: "number",
      row: 2,
      value: "",
      validation: {
        required: true,
        isNumeric: true,
        notZero: true,
      },
    },
    description: {
      type: "text",
      row: 2,
      value: "",
      validation: null,
    },
    periods: {
      type: "select",
      row: 1,
      selectItems: [],
      value: "",
      validation: {
        required: true,
      },
    },
    groups: {
      type: "select",
      row: 1,
      selectItems: [],
      value: "",
      validation: {
        required: true,
      },
    },
    options: {
      type: "checkbox",
      row: 1,
      items: [{ value: false, key: "freePeriod" }],
    },
  });

  const onEnter = async () => {
    setLoading(true);
    await getPeriods();
  };

  const getPeriods = async () => {
    return new Promise(async (resolve) => {
      const params = {
        KeyGroup: read("groupAKey"),
        KeyUser: read("userAKey"),
      };
      await dispatch(getActivePeriods(params));
      resolve(true);
    });
  };

  useEffect(() => {
    if (activePeriods) {
      const selectItems = activePeriods.map((child) => {
        return {
          label: child.Name,
          value: child.Id,
        };
      });
      setControls({ periods: { selectItems } });
      setControls({ periods: { value: selectItems[0] } });
    }
    if (allGroups) {
      const selectItems = allGroups.map((child) => {
        return {
          label: child.Name,
          value: child.Id,
        };
      });
      setControls({ groups: { selectItems } });
      setControls({ groups: { value: selectItems[0] } });
    }
    setLoading(false);
  }, [activePeriods, allGroups]);

  const setValue = (name, value, validation) => {
    setControls({ [name]: { value: value } });
    if (name !== " nameSearch")
      setFormValid((prev) => ({ ...prev, [name]: validation }));
  };

  const autoCompleteHandler = async (e, value) => {
    const controlName = e.target.id.split("-")[0];
    setControls({ [controlName]: { value: value } });
  };

  const checkboxHandler = (e, name, index) => {
    const items = controls[name].items;
    items[index] = { ...items[index], value: e.currentTarget.checked };
    setControls({ [name]: { items: items } });
  };

  const onTableChange = async (type, { page, sizePerPage }) => {
    if (type === "pagination") {
      await search(page, sizePerPage);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    await search();
  };

  const search = async (_page = 1, _sizePerPage = sizePerPage) => {
    setLoading(true);
    setIds([]);
    setAllowAction(false);
    return new Promise(async (resolve) => {
      try {
        const { periods, groups, options } = controls;
        const params = {
          KeyGroup: read("groupAKey"),
          KeyUser: read("userAKey"),
          PeriodId: periods.value.value,
          GroupId: groups.value.value,
          IsFree: options.items[0].value,
        };
        const response = await AccountService.getAccountingHistoryActive(
          params
        );
        let err = StatusHandler(response);
        if (!err.isFail) {
          setTblData(addNumToList(response.data.Value, 1, 0));
          setSizePerPage(response.data.Pagination.Limit);
          setCurrentPage(response.data.Pagination.Page);
          setTotal(response.data.Pagination.Total);
        }
      } catch (e) {
        showNotify({ text: "getDataError", customClass: "error" });
      }
      setTimeout(() => {
        resolve(true);
        setLoading(false);
      }, 500);
    });
  };

  const onSelectRow = {
    mode: "checkbox",
    clickToSelect: true,
    hideSelectColumn: false,
    hideSelectAll: true,
    classes: "selection-row",
    onSelect: (row, isSelect, rowIndex, e) => {
      let _Ids = ids;
      if (isSelect) {
        _Ids.push(row.Id);
      } else {
        _Ids = _Ids.filter((item) => item !== row.Id);
      }
      setIds(_Ids);
      setAllowAction(_Ids.length === 0 ? false : true);
    },
    selectionRenderer: ({ checked, disabled }) => {
      return (
        <Checkbox
          className="selectRadio"
          color="default"
          checked={checked}
          disabled={disabled}
        />
      );
    },
  };

  const handleSaveIncTime = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const { description, hoursNumber } = controls;
      if (
        !R.isEmpty(formValid) &&
        Object.values(formValid).every((item) => item === true)
      ) {
        const data = {
          KeyGroup: read("groupAKey"),
          KeyUser: read("userAKey"),
          Description: description.value,
          Hours: +convertToEnglishNumber(hoursNumber.value),
          Ids: ids,
        };
        const response = await AccountService.postAccountingHistoryIncTime(
          data
        );
        let err = StatusHandler(response);
        if (!err.isFail) {
          if (response.data.Value.IsError === true) {
            showNotify({
              text: response.data.Value.Describ,
              customClass: "error",
            });
          } else {
            showNotify({
              text: t("mission_accomplished"),
              customClass: "success",
            });
            onCallBack();
            onExit();
          }
          setLoading(false);
        }
      } else {
        setCheckValid(true);
        setLoading(false);
      }
    } catch (err) {
      console.log("[NewIncTime]", err);
      setLoading(false);
    }
  };

  const onExit = () => {
    setControls({ description: { value: "" } });
    setControls({ hoursNumber: { value: "" } });
    controls.options.items.map((items) => (items.value = false));
    setFormValid({ description: false, hoursNumber: false });
    setTblData([]);
    setIds([]);
    setAllowAction(false);
    setShowDialog(false);
  };

  return (
    <>
      <MuiButton
        color="primary"
        title={<Translator id="newIncTime" />}
        innerIcon={<Icons.PlusLg />}
        onClick={() => setShowDialog(true)}
        disabled={disabled || !read("FormIncTime").accessRegister}
        hidden={!read("FormIncTime").accessRegister}
      />
      <Dialog
        dir={direction}
        open={showDialog}
        onClose={onExit}
        TransitionProps={{ onEnter: onEnter, onExit: onExit }}
        fullWidth
        maxWidth="md"
      >
        <div className="dialogHeader justify-content-start">
          <p>
            <Translator id="newIncTime" />
          </p>
        </div>
        <DialogContent>
          <Grid container spacing={2}>
            {Object.keys(controls)
              .filter((key) => controls[key].row === 1)
              .map((key) => {
                return (
                  <Grid
                    item
                    className="flex-grow-1"
                    style={{
                      minWidth:
                        controls[key].type === "select" ? "220px" : "unset",
                    }}
                    key={key}
                  >
                    {controls[key].type === "select" && (
                      <MuiAutoComplete
                        autoSelect={true}
                        options={controls[key].selectItems}
                        label={<Translator id={key} />}
                        value={controls[key].value}
                        onChange={autoCompleteHandler}
                        id={key}
                        disabled={loading}
                      />
                    )}
                    {controls[key].type === "checkbox" && (
                      <MuiCheckBoxGroup
                        translate={true}
                        name={key}
                        value={controls[key].value}
                        items={controls[key].items}
                        onChange={checkboxHandler}
                        className="horizontalCheckbox"
                        disabled={loading}
                      />
                    )}
                  </Grid>
                );
              })}
            <Grid item className="flex-grow-1 margin-top-button no-sm-effect">
              <MuiButton
                color="secondary"
                title={<Translator id="doFilter" />}
                innerIcon={<Icons.Funnel />}
                onClick={handleSearch}
                disabled={loading}
                fullwidthOnSm
              />
            </Grid>
            <Grid item xs={12}>
              {!loading ? (
                <RemoteTable
                  data={tblData}
                  columns={satc_accountingHistoryActive}
                  page={currentPage}
                  sizePerPage={sizePerPage}
                  totalSize={total}
                  onTableChange={onTableChange}
                  selectRow={onSelectRow}
                  showPagination={false}
                />
              ) : (
                <InnerCircularProgress />
              )}
            </Grid>
            {allowAction && (
              <Grid container spacing={2}>
                {Object.keys(controls)
                  .filter((key) => controls[key].row === 2)
                  .map((key) => {
                    return (
                      <Grid item sm={6} xs={12} key={key}>
                        {(controls[key].type === "number" ||
                          controls[key].type === "text") && (
                          <Input
                            name={key}
                            label={<Translator id={key} />}
                            setValue={setValue.bind(this)}
                            validation={controls[key].validation}
                            placeholder={t(key)}
                            type={controls[key].type}
                            checkValid={checkValid}
                            value={controls[key].value}
                            readOnly={loading}
                          />
                        )}
                      </Grid>
                    );
                  })}
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          {allowAction && (
            <MuiButton
              color="primary"
              title={<Translator id="button_save" />}
              disabled={loading}
              onClick={handleSaveIncTime}
              fullwidthOnSm
            />
          )}
          <MuiButton
            onClick={() => setShowDialog(false)}
            disabled={loading}
            title={<Translator id="close" />}
          />
        </DialogActions>
      </Dialog>
    </>
  );
}
